import type { Slot } from "./types";

/**
 * Un groupe de jour pour le day-banner row de la ResultsTable.
 * Les slots consécutifs partageant la même `primaryDate` (ou même
 * `groupLabel` pour les groupes multi-jours) sont fusionnés en une cellule
 * qui couvre `span` colonnes.
 */
export interface DayGroup {
  /** Clé stable : groupLabel s'il existe, sinon primaryDate */
  key: string;
  /** Label affiché (groupLabel s'il existe, sinon primaryDate) */
  label: string;
  /** Index du premier slot du groupe dans la liste d'origine */
  startIndex: number;
  /** Nombre de slots couverts (≥ 1) */
  span: number;
  /** IDs des slots couverts, dans l'ordre */
  slotIds: string[];
}

/**
 * Groupe les slots consécutifs partageant le même jour pour la ligne
 * day-banner du tableau résultats.
 *
 * Règles :
 * - Slots avec `groupLabel` sont leur propre groupe (jamais fusionnés)
 * - Slots mono-date sont fusionnés si `primaryDate` est identique au précédent
 * - Ordre préservé : on n'effectue aucun tri, l'appelant garantit déjà l'ordre
 *   chronologique des slots.
 */
export function groupSlotsByDay(slots: Slot[]): DayGroup[] {
  const groups: DayGroup[] = [];
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    const key = slot.groupLabel ?? slot.primaryDate;
    const label = slot.groupLabel ?? slot.primaryDate;
    const last = groups[groups.length - 1];
    // Un slot avec groupLabel n'est jamais fusionné avec un voisin (même
    // s'il partage la primaryDate) — c'est un groupe sémantique distinct.
    const canMerge =
      last &&
      !slot.groupLabel &&
      last.label === label &&
      // Si le précédent était un groupLabel, on ne fusionne pas non plus.
      slots[last.startIndex + last.span - 1].groupLabel === undefined;
    if (canMerge) {
      last.span += 1;
      last.slotIds.push(slot.id);
    } else {
      groups.push({
        key,
        label,
        startIndex: i,
        span: 1,
        slotIds: [slot.id],
      });
    }
  }
  return groups;
}
